import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, Collapse, Descriptions, Table, Button, Spin, Tag, Space, Row, Col } from 'antd';
import { ArrowLeftOutlined, FilePdfOutlined } from '@ant-design/icons';
import { PageTitle, StandardCard } from '../common';
import { theme } from '../../theme';
import api from '../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';

const { Panel } = Collapse;

interface EmisionRecdesData {
  nro_recdes: number;
  tipo: 'R' | 'D';
  fecha: string;
  buque: string;
  marca: string;
  observacion: string;
  estado: string;
  empresa: {
    nombre: string;
    cuit: string;
    direccion: string;
  };
  sucursal: {
    nombre: string;
    direccion: string;
  };
  orden: {
    nro_orden: number;
    fecha: string;
    tipo: string;
    tipo_display: string;
    nro_contrato: string;
    comprador: {
      nombre: string;
      cuit: string;
      direccion: string;
    };
    vendedor: {
      nombre: string;
      cuit: string;
      direccion: string;
    };
    destino: {
      nombre: string;
      cuit: string;
      direccion: string;
    };
    producto: {
      descripcion: string;
      codigo: string;
    };
  };
  destino_especifico: {
    empresa: string;
    direccion: string;
    localidad: string;
    provincia: string;
  } | null;
  balanza: {
    numero_ticket: number;
    fecha_ticket: string;
    kilos_primera: number;
    kilos_segunda: number;
    kilos_netos: number;
    humedad: number;
    numero_remito: string;
    dtv: string;
    cliente: {
      nombre: string;
      cuit: string;
    };
    productor: {
      nombre: string;
      cuit: string;
    };
    transportista: {
      nombre: string;
      cuit: string;
    };
    chofer: {
      nombre: string;
      cuit: string;
      numero_carnet: string;
      vencimiento_carnet: string;
      contrato_art: string;
      vencimiento_art: string;
    } | null;
    camion: {
      patente_camion: string;
      patente_acoplado: string;
    } | null;
    aotrasucursal_nombre?: string;
  };
  ciclo: {
    nombre: string;
    descripcion: string;
  };
  detalles: Array<{
    nro_fardo: number;
    nro_lote: number;
    silobolsa: number;
    numero_silo: number;
    partesilobolsa: number;
    kilos_bolson: number;
    origen: {
      nombre: string;
      cuit: string;
    };
    destino: {
      nombre: string;
      cuit: string;
    };
  }>;
  totales: {
    cantidad_fardos: number;
    kilos_netos: number;
    cantidad_detalles: number;
  };
}

const EmisionRecdes: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [data, setData] = useState<EmisionRecdesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('useEffect ejecutado');
    console.log('id_recdes:', id);
    console.log('user:', user);
    
    if (id && user?.id_empresa) {
      console.log('Condiciones cumplidas, cargando datos...');
      cargarDatosEmision();
    } else if (id && !user?.id_empresa) {
      console.log('Esperando a que el usuario esté disponible...');
    } else if (!id) {
      console.log('No hay id_recdes disponible');
      setError('No se proporcionó un ID de recepción/despacho');
      setLoading(false);
    }
  }, [id, user]);

  const cargarDatosEmision = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Iniciando carga de datos de emisión...');
      console.log('id_recdes:', id);
      console.log('user:', user);
      console.log('user?.id_empresa:', user?.id_empresa);
      
      if (!user?.id_empresa) {
        throw new Error('No se pudo obtener el ID de empresa del usuario');
      }
      
      if (!process.env.REACT_APP_ORDENES_URL) {
        throw new Error('La variable de entorno REACT_APP_ORDENES_URL no está configurada');
      }
      
      const token = await getAccessTokenSilently();
      console.log('Token obtenido:', token ? 'Sí' : 'No');
      
      const url = `${process.env.REACT_APP_ORDENES_URL}/emision_recepcion_despacho/${user.id_empresa}/${id}/`;
      console.log('URL de la petición:', url);
      console.log('REACT_APP_ORDENES_URL:', process.env.REACT_APP_ORDENES_URL);
      console.log('URL completa construida:', url);
      
      const response = await api.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Respuesta del servidor:', response.data);
      setData(response.data);
    } catch (error: any) {
      console.error('Error completo al cargar datos de emisión:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      setError(error.response?.data?.error || error.message || 'Error al cargar los datos de emisión');
    } finally {
      setLoading(false);
    }
  };

  const formatFecha = (fecha: string) => {
    try {
      const date = new Date(fecha);
      return date.toLocaleDateString('es-ES');
    } catch {
      return fecha;
    }
  };

  const formatCuit = (cuit: string) => {
    if (!cuit) return '-';
    return cuit.replace(/(\d{2})(\d{8})(\d{1})/, '$1-$2-$3');
  };

  const getTipoBadge = (tipo: string) => {
    return tipo === 'R' ? (
      <Tag color="success">Recepción</Tag>
    ) : (
      <Tag color="processing">Despacho</Tag>
    );
  };

  const generarPDF = () => {
    if (!data) return;

    // Crear nuevo documento PDF
    const doc = new jsPDF();
    
    // Configurar fuente y tamaños
    doc.setFont('helvetica');
    doc.setFontSize(16);
    
    // Título principal
    const titulo = `${data.tipo === 'R' ? 'RECEPCIÓN' : 'DESPACHO'} N° ${data.nro_recdes}`;
    doc.text(titulo, 105, 20, { align: 'center' });
    
    let yPosition = 40;
    
    // DATOS DE LA EMPRESA
    doc.setFontSize(14);
    doc.setTextColor(135, 206, 235); // Color azul claro como en pantalla
    doc.text('DATOS DE LA EMPRESA', 20, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    yPosition += 15;
    
         doc.text(`Empresa: ${data.empresa?.nombre || 'No especificado'}`, 20, yPosition);
     yPosition += 8;
     doc.text(`CUIT: ${formatCuit(data.empresa?.cuit) || 'No especificado'}`, 20, yPosition);
     yPosition += 8;
     doc.text(`Dirección: ${data.empresa?.direccion || 'No especificado'}`, 20, yPosition);
     yPosition += 8;
     doc.text(`Sucursal: ${data.sucursal?.nombre || 'No especificado'}`, 20, yPosition);
    
    yPosition += 15;
    
    // DATOS DEL DOCUMENTO
    doc.setFontSize(14);
    doc.setTextColor(135, 206, 235);
    doc.text('DATOS DEL DOCUMENTO', 20, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    yPosition += 15;
    
    doc.text(`Número: ${data.nro_recdes}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Tipo: ${data.tipo === 'R' ? 'Recepción' : 'Despacho'}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Fecha: ${dayjs(data.fecha).format('DD/MM/YYYY')}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Estado: ${data.estado}`, 20, yPosition);
    
    yPosition += 15;
    
    // DATOS DE LA ORDEN
    doc.setFontSize(14);
    doc.setTextColor(135, 206, 235);
    doc.text('DATOS DE LA ORDEN', 20, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    yPosition += 15;
    
    doc.text(`N° Orden: ${data.orden.nro_orden}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Fecha Orden: ${dayjs(data.orden.fecha).format('DD/MM/YYYY')}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Tipo Orden: ${data.orden.tipo_display}`, 20, yPosition);
    yPosition += 8;
    doc.text(`N° Contrato: ${data.orden.nro_contrato || 'No especificado'}`, 20, yPosition);
    yPosition += 8;
         doc.text(`Producto: ${data.orden.producto?.descripcion || 'No especificado'}`, 20, yPosition);
    
    yPosition += 15;
    
    // DATOS SUCURSAL INGRESO (solo para tipo A - Otra Sucursal)
    if (data.orden.tipo === 'A' && data.balanza.aotrasucursal_nombre) {
      doc.setFontSize(14);
      doc.setTextColor(135, 206, 235);
      doc.text('DATOS SUCURSAL INGRESO', 20, yPosition);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      yPosition += 15;
      
             doc.text(`Nombre Sucursal: ${data.balanza.aotrasucursal_nombre}`, 20, yPosition);
       yPosition += 15;
    }
    
    // DATOS DE INTERVINIENTES
    doc.setFontSize(14);
    doc.setTextColor(135, 206, 235);
    doc.text('DATOS DE INTERVINIENTES', 20, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    yPosition += 15;
    
         doc.text(`Comprador: ${data.orden.comprador?.nombre || 'No especificado'}`, 20, yPosition);
     yPosition += 8;
     doc.text(`CUIT Comprador: ${formatCuit(data.orden.comprador?.cuit) || 'No especificado'}`, 20, yPosition);
     yPosition += 8;
     doc.text(`Vendedor: ${data.orden.vendedor?.nombre || 'No especificado'}`, 20, yPosition);
     yPosition += 8;
     doc.text(`CUIT Vendedor: ${formatCuit(data.orden.vendedor?.cuit) || 'No especificado'}`, 20, yPosition);
     yPosition += 8;
     doc.text(`Destino: ${data.orden.destino?.nombre || 'No especificado'}`, 20, yPosition);
     yPosition += 8;
     doc.text(`CUIT Destino: ${formatCuit(data.orden.destino?.cuit) || 'No especificado'}`, 20, yPosition);
    
    yPosition += 15;
    
    // DATOS DEL DESTINO ESPECÍFICO (si existe)
    if (data.destino_especifico) {
      doc.setFontSize(14);
      doc.setTextColor(135, 206, 235);
      doc.text('DATOS DEL DESTINO ESPECÍFICO', 20, yPosition);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      yPosition += 15;
      
      doc.text(`Empresa: ${data.destino_especifico.empresa}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Dirección: ${data.destino_especifico.direccion}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Localidad: ${data.destino_especifico.localidad}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Provincia: ${data.destino_especifico.provincia}`, 20, yPosition);
      yPosition += 15;
    }
    
    // Nueva página para datos de balanza
    doc.addPage();
    doc.setFontSize(14);
    doc.setTextColor(135, 206, 235);
    doc.text('DATOS DE BALANZA', 105, 20, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    
    yPosition = 40;
    doc.setFontSize(10);
    
    doc.text(`N° Ticket: ${data.balanza.numero_ticket}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Fecha Ticket: ${dayjs(data.balanza.fecha_ticket).format('DD/MM/YYYY')}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Kilos Primera: ${data.balanza.kilos_primera.toLocaleString('es-ES', {minimumFractionDigits: 2})} kg`, 20, yPosition);
    yPosition += 8;
    doc.text(`Kilos Segunda: ${data.balanza.kilos_segunda.toLocaleString('es-ES', {minimumFractionDigits: 2})} kg`, 20, yPosition);
    yPosition += 8;
    doc.text(`Kilos Netos: ${data.balanza.kilos_netos.toLocaleString('es-ES', {minimumFractionDigits: 2})} kg`, 20, yPosition);
    yPosition += 8;
    doc.text(`Humedad: ${data.balanza.humedad.toLocaleString('es-ES', {minimumFractionDigits: 2})}%`, 20, yPosition);
    yPosition += 8;
    doc.text(`N° Remito: ${data.balanza.numero_remito || 'No especificado'}`, 20, yPosition);
    yPosition += 8;
    doc.text(`DTV: ${data.balanza.dtv || 'No especificado'}`, 20, yPosition);
    
    yPosition += 15;
    
    // DATOS DE TRANSPORTE
    doc.setFontSize(14);
    doc.setTextColor(135, 206, 235);
    doc.text('DATOS DE TRANSPORTE', 20, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    yPosition += 15;
    
         doc.text(`Cliente: ${data.balanza.cliente?.nombre || 'No especificado'}`, 20, yPosition);
     yPosition += 8;
     doc.text(`CUIT Cliente: ${formatCuit(data.balanza.cliente?.cuit) || 'No especificado'}`, 20, yPosition);
     yPosition += 8;
     doc.text(`Productor: ${data.balanza.productor?.nombre || 'No especificado'}`, 20, yPosition);
     yPosition += 8;
     doc.text(`CUIT Productor: ${formatCuit(data.balanza.productor?.cuit) || 'No especificado'}`, 20, yPosition);
     yPosition += 8;
     doc.text(`Transportista: ${data.balanza.transportista?.nombre || 'No especificado'}`, 20, yPosition);
     yPosition += 8;
     doc.text(`CUIT Transportista: ${formatCuit(data.balanza.transportista?.cuit) || 'No especificado'}`, 20, yPosition);
    
    yPosition += 15;
    
    // DATOS DEL CHOFER (si existe)
    if (data.balanza.chofer) {
      doc.setFontSize(14);
      doc.setTextColor(135, 206, 235);
      doc.text('DATOS DEL CHOFER', 20, yPosition);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      yPosition += 15;
      
      doc.text(`Nombre: ${data.balanza.chofer.nombre}`, 20, yPosition);
      yPosition += 8;
      doc.text(`CUIT: ${formatCuit(data.balanza.chofer.cuit)}`, 20, yPosition);
      yPosition += 8;
      doc.text(`N° Carnet: ${data.balanza.chofer.numero_carnet}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Vencimiento Carnet: ${dayjs(data.balanza.chofer.vencimiento_carnet).format('DD/MM/YYYY')}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Contrato ART: ${data.balanza.chofer.contrato_art}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Vencimiento ART: ${dayjs(data.balanza.chofer.vencimiento_art).format('DD/MM/YYYY')}`, 20, yPosition);
      yPosition += 15;
    }
    
    // DATOS DEL CAMIÓN (si existe)
    if (data.balanza.camion) {
      doc.setFontSize(14);
      doc.setTextColor(135, 206, 235);
      doc.text('DATOS DEL CAMIÓN', 20, yPosition);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      yPosition += 15;
      
      doc.text(`Patente Camión: ${data.balanza.camion.patente_camion}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Patente Acoplado: ${data.balanza.camion.patente_acoplado}`, 20, yPosition);
      yPosition += 15;
    }
    
    // DATOS DEL CICLO
    doc.setFontSize(14);
    doc.setTextColor(135, 206, 235);
    doc.text('DATOS DEL CICLO', 20, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    yPosition += 15;
    
         doc.text(`Nombre: ${data.ciclo?.nombre || 'No especificado'}`, 20, yPosition);
     yPosition += 8;
     doc.text(`Descripción: ${data.ciclo?.descripcion || 'No especificado'}`, 20, yPosition);
    
    // Nueva página para detalles
    doc.addPage();
    doc.setFontSize(14);
    doc.setTextColor(135, 206, 235);
    doc.text('DETALLES DE LA ORDEN', 105, 20, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    
    // Tabla de detalles
    if (data.detalles.length > 0) {
      const detallesData = data.detalles.map(detalle => [
        detalle.nro_fardo,
        detalle.nro_lote,
        detalle.numero_silo,
        detalle.partesilobolsa,
        detalle.kilos_bolson,
                 detalle.origen?.nombre || 'No especificado',
         detalle.destino?.nombre || 'No especificado'
      ]);
      
      autoTable(doc, {
        head: [['Nro Fardo', 'Nro Lote', 'Nro Bolsón', 'Parte Bolsón', 'Kilos', 'Origen', 'Destino']],
        body: detallesData,
        startY: 30,
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [135, 206, 235],
          textColor: 255,
          fontStyle: 'bold',
        },
      });
    }
    
    // Resumen al final
    const finalY = (doc as any).lastAutoTable ? (doc as any).lastAutoTable.finalY + 15 : 200;
    doc.setFontSize(12);
    doc.setTextColor(135, 206, 235);
    doc.text('RESUMEN:', 20, finalY);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Total Fardos: ${data.totales.cantidad_fardos}`, 20, finalY + 10);
    doc.text(`Total Kilos Netos: ${data.totales.kilos_netos.toLocaleString('es-ES', {minimumFractionDigits: 2})} kg`, 20, finalY + 18);
    doc.text(`Cantidad Detalles: ${data.totales.cantidad_detalles}`, 20, finalY + 26);
    
    // Observaciones (si existen)
    if (data.observacion) {
      doc.text(`Observaciones: ${data.observacion}`, 20, finalY + 40);
    }
    
    // Datos adicionales
    doc.text(`Buque: ${data.buque || 'No especificado'}`, 20, finalY + 50);
    doc.text(`Marca: ${data.marca || 'No especificado'}`, 20, finalY + 58);
    
    // Guardar PDF
    const fileName = `${data.tipo === 'R' ? 'Recepcion' : 'Despacho'}_${data.nro_recdes}_${dayjs().format('YYYY-MM-DD')}.pdf`;
    doc.save(fileName);
  };

  const volver = () => {
    navigate('/listado_recepcion_despacho');
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Spin size="large" tip="Cargando datos de emisión..." />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: '40px' }}>
        <StandardCard 
          title={error ? 'Error' : 'No se encontraron datos'}
          bodyStyle={{ padding: '24px' }}
        >
          <p style={{ marginBottom: '16px', fontSize: '13px' }}>
            {error || 'No se pudieron cargar los datos de emisión.'}
          </p>
          <Button type="primary" onClick={volver} size="middle" style={{ height: '32px', fontSize: '13px' }}>
            Volver al listado
          </Button>
        </StandardCard>
      </div>
    );
  }

  // Configuración de columnas para la tabla de detalles
  const detallesColumns = [
    {
      title: 'N° Fardo',
      dataIndex: 'nro_fardo',
      key: 'nro_fardo',
      width: 100,
      align: 'center' as const,
    },
    {
      title: 'N° Lote',
      dataIndex: 'nro_lote',
      key: 'nro_lote',
      width: 100,
      align: 'center' as const,
    },
    {
      title: 'N° Bolsón',
      dataIndex: 'numero_silo',
      key: 'numero_silo',
      width: 100,
      align: 'center' as const,
      render: (value: number) => value || '-',
    },
    {
      title: 'Parte Bolsón',
      dataIndex: 'partesilobolsa',
      key: 'partesilobolsa',
      width: 120,
      align: 'center' as const,
      render: (value: number) => value || '-',
    },
    {
      title: 'Kilos Bolsón',
      dataIndex: 'kilos_bolson',
      key: 'kilos_bolson',
      width: 120,
      align: 'right' as const,
      render: (value: number) => value?.toLocaleString('es-ES', { minimumFractionDigits: 2 }) || '-',
    },
    {
      title: 'Origen',
      dataIndex: ['origen', 'nombre'],
      key: 'origen',
      width: 200,
    },
    {
      title: 'Destino',
      dataIndex: ['destino', 'nombre'],
      key: 'destino',
      width: 200,
    },
  ];

  return (
    <div style={{ padding: '0' }}>
      {/* Header con título y botones */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <PageTitle style={{ marginBottom: 0 }}>
          {data.tipo === 'R' ? 'RECEPCIÓN' : 'DESPACHO'} N° {data.nro_recdes}
        </PageTitle>
        <Space>
          <Button 
            type="primary" 
            icon={<FilePdfOutlined />}
            onClick={generarPDF} 
            size="middle"
            style={{ height: '32px', fontSize: '13px' }}
          >
            Generar PDF
          </Button>
          <Button 
            icon={<ArrowLeftOutlined />}
            onClick={volver} 
            size="middle"
            style={{ height: '32px', fontSize: '13px' }}
          >
            Volver
          </Button>
        </Space>
      </div>

      {/* Tabs principales */}
      <StandardCard bodyStyle={{ padding: '16px' }}>
        <Tabs
          defaultActiveKey="general"
          items={[
            {
              key: 'general',
              label: 'Información General',
              children: (
                <div style={{ paddingTop: '12px' }}>
                  {/* Datos de la Empresa y Documento */}
                  <Collapse defaultActiveKey={['empresa', 'documento']} ghost>
                    <Panel header="Datos de la Empresa" key="empresa">
                      <Descriptions 
                        column={{ xs: 1, sm: 2, md: 2 }}
                        size="small"
                        labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                        contentStyle={{ fontSize: '13px' }}
                      >
                        <Descriptions.Item label="Empresa">{data.empresa?.nombre || '-'}</Descriptions.Item>
                        <Descriptions.Item label="CUIT">{formatCuit(data.empresa?.cuit) || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Dirección" span={2}>{data.empresa?.direccion || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Sucursal">{data.sucursal?.nombre || '-'}</Descriptions.Item>
                      </Descriptions>
                    </Panel>
                    <Panel header="Datos del Documento" key="documento">
                      <Descriptions 
                        column={{ xs: 1, sm: 2, md: 2 }}
                        size="small"
                        labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                        contentStyle={{ fontSize: '13px' }}
                      >
                        <Descriptions.Item label="Número">{data.nro_recdes}</Descriptions.Item>
                        <Descriptions.Item label="Tipo">{getTipoBadge(data.tipo)}</Descriptions.Item>
                        <Descriptions.Item label="Fecha">{formatFecha(data.fecha)}</Descriptions.Item>
                        <Descriptions.Item label="Estado">{data.estado}</Descriptions.Item>
                      </Descriptions>
                    </Panel>
                  </Collapse>

                  {/* Datos de la Orden */}
                  <Collapse defaultActiveKey={['orden']} ghost style={{ marginTop: '12px' }}>
                    <Panel header="Datos de la Orden" key="orden">
                      <Descriptions 
                        column={{ xs: 1, sm: 2, md: 2 }}
                        size="small"
                        labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                        contentStyle={{ fontSize: '13px' }}
                      >
                        <Descriptions.Item label="N° Orden">{data.orden.nro_orden}</Descriptions.Item>
                        <Descriptions.Item label="Fecha Orden">{formatFecha(data.orden.fecha)}</Descriptions.Item>
                        <Descriptions.Item label="Tipo Orden">{data.orden.tipo_display}</Descriptions.Item>
                        <Descriptions.Item label="N° Contrato">{data.orden.nro_contrato || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Producto">{data.orden.producto?.descripcion || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Código">{data.orden.producto?.codigo || '-'}</Descriptions.Item>
                      </Descriptions>
                      {data.orden.tipo === 'A' && data.balanza.aotrasucursal_nombre && (
                        <div style={{ marginTop: '12px' }}>
                          <Descriptions 
                            column={{ xs: 1, sm: 2, md: 2 }}
                            size="small"
                            labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                            contentStyle={{ fontSize: '13px' }}
                          >
                            <Descriptions.Item label="Sucursal Ingreso" span={2}>
                              {data.balanza.aotrasucursal_nombre}
                            </Descriptions.Item>
                          </Descriptions>
                        </div>
                      )}
                    </Panel>
                  </Collapse>

                  {/* Intervinientes */}
                  <Collapse defaultActiveKey={['intervinientes']} ghost style={{ marginTop: '12px' }}>
                    <Panel header="Intervinientes" key="intervinientes">
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={8}>
                          <Descriptions 
                            column={1}
                            size="small"
                            title="Comprador"
                            labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                            contentStyle={{ fontSize: '13px' }}
                          >
                            <Descriptions.Item label="Nombre">{data.orden.comprador?.nombre || '-'}</Descriptions.Item>
                            <Descriptions.Item label="CUIT">{formatCuit(data.orden.comprador?.cuit) || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Dirección">{data.orden.comprador?.direccion || '-'}</Descriptions.Item>
                          </Descriptions>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Descriptions 
                            column={1}
                            size="small"
                            title="Vendedor"
                            labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                            contentStyle={{ fontSize: '13px' }}
                          >
                            <Descriptions.Item label="Nombre">{data.orden.vendedor?.nombre || '-'}</Descriptions.Item>
                            <Descriptions.Item label="CUIT">{formatCuit(data.orden.vendedor?.cuit) || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Dirección">{data.orden.vendedor?.direccion || '-'}</Descriptions.Item>
                          </Descriptions>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Descriptions 
                            column={1}
                            size="small"
                            title="Destino"
                            labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                            contentStyle={{ fontSize: '13px' }}
                          >
                            <Descriptions.Item label="Nombre">{data.orden.destino?.nombre || '-'}</Descriptions.Item>
                            <Descriptions.Item label="CUIT">{formatCuit(data.orden.destino?.cuit) || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Dirección">{data.orden.destino?.direccion || '-'}</Descriptions.Item>
                          </Descriptions>
                        </Col>
                      </Row>
                    </Panel>
                  </Collapse>

                  {/* Destino Específico */}
                  {data.destino_especifico && (
                    <Collapse ghost style={{ marginTop: '12px' }}>
                      <Panel header="Destino Específico" key="destino-especifico">
                        <Descriptions 
                          column={{ xs: 1, sm: 2, md: 2 }}
                          size="small"
                          labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                          contentStyle={{ fontSize: '13px' }}
                        >
                          <Descriptions.Item label="Empresa">{data.destino_especifico.empresa}</Descriptions.Item>
                          <Descriptions.Item label="Dirección">{data.destino_especifico.direccion}</Descriptions.Item>
                          <Descriptions.Item label="Localidad">{data.destino_especifico.localidad}</Descriptions.Item>
                          <Descriptions.Item label="Provincia">{data.destino_especifico.provincia}</Descriptions.Item>
                        </Descriptions>
                      </Panel>
                    </Collapse>
                  )}
                </div>
              ),
            },
            {
              key: 'balanza',
              label: 'Balanza y Transporte',
              children: (
                <div style={{ paddingTop: '12px' }}>
                  {/* Datos de Balanza */}
                  <Collapse defaultActiveKey={['balanza']} ghost>
                    <Panel header="Datos de Balanza" key="balanza">
                      <Descriptions 
                        column={{ xs: 1, sm: 2, md: 2 }}
                        size="small"
                        labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                        contentStyle={{ fontSize: '13px' }}
                      >
                        <Descriptions.Item label="N° Ticket">{data.balanza.numero_ticket}</Descriptions.Item>
                        <Descriptions.Item label="Fecha Ticket">{formatFecha(data.balanza.fecha_ticket)}</Descriptions.Item>
                        <Descriptions.Item label="Kilos Primera">
                          {data.balanza.kilos_primera.toLocaleString('es-ES', { minimumFractionDigits: 2 })} kg
                        </Descriptions.Item>
                        <Descriptions.Item label="Kilos Segunda">
                          {data.balanza.kilos_segunda.toLocaleString('es-ES', { minimumFractionDigits: 2 })} kg
                        </Descriptions.Item>
                        <Descriptions.Item label="Kilos Netos">
                          <strong>{data.balanza.kilos_netos.toLocaleString('es-ES', { minimumFractionDigits: 2 })} kg</strong>
                        </Descriptions.Item>
                        <Descriptions.Item label="Humedad">
                          {data.balanza.humedad.toLocaleString('es-ES', { minimumFractionDigits: 2 })}%
                        </Descriptions.Item>
                        <Descriptions.Item label="N° Remito">{data.balanza.numero_remito || '-'}</Descriptions.Item>
                        <Descriptions.Item label="DTV">{data.balanza.dtv || '-'}</Descriptions.Item>
                      </Descriptions>
                    </Panel>
                  </Collapse>

                  {/* Datos de Transporte */}
                  <Collapse defaultActiveKey={['transporte']} ghost style={{ marginTop: '12px' }}>
                    <Panel header="Datos de Transporte" key="transporte">
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={8}>
                          <Descriptions 
                            column={1}
                            size="small"
                            title="Cliente"
                            labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                            contentStyle={{ fontSize: '13px' }}
                          >
                            <Descriptions.Item label="Nombre">{data.balanza.cliente?.nombre || '-'}</Descriptions.Item>
                            <Descriptions.Item label="CUIT">{formatCuit(data.balanza.cliente?.cuit) || '-'}</Descriptions.Item>
                          </Descriptions>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Descriptions 
                            column={1}
                            size="small"
                            title="Productor"
                            labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                            contentStyle={{ fontSize: '13px' }}
                          >
                            <Descriptions.Item label="Nombre">{data.balanza.productor?.nombre || '-'}</Descriptions.Item>
                            <Descriptions.Item label="CUIT">{formatCuit(data.balanza.productor?.cuit) || '-'}</Descriptions.Item>
                          </Descriptions>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Descriptions 
                            column={1}
                            size="small"
                            title="Transportista"
                            labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                            contentStyle={{ fontSize: '13px' }}
                          >
                            <Descriptions.Item label="Nombre">{data.balanza.transportista?.nombre || '-'}</Descriptions.Item>
                            <Descriptions.Item label="CUIT">{formatCuit(data.balanza.transportista?.cuit) || '-'}</Descriptions.Item>
                          </Descriptions>
                        </Col>
                      </Row>
                    </Panel>
                  </Collapse>

                  {/* Datos del Chofer */}
                  {data.balanza.chofer && (
                    <Collapse ghost style={{ marginTop: '12px' }}>
                      <Panel header="Datos del Chofer" key="chofer">
                        <Descriptions 
                          column={{ xs: 1, sm: 2, md: 2 }}
                          size="small"
                          labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                          contentStyle={{ fontSize: '13px' }}
                        >
                          <Descriptions.Item label="Nombre">{data.balanza.chofer.nombre}</Descriptions.Item>
                          <Descriptions.Item label="CUIT">{formatCuit(data.balanza.chofer.cuit)}</Descriptions.Item>
                          <Descriptions.Item label="N° Carnet">{data.balanza.chofer.numero_carnet}</Descriptions.Item>
                          <Descriptions.Item label="Vencimiento Carnet">{formatFecha(data.balanza.chofer.vencimiento_carnet)}</Descriptions.Item>
                          <Descriptions.Item label="Contrato ART">{data.balanza.chofer.contrato_art}</Descriptions.Item>
                          <Descriptions.Item label="Vencimiento ART">{formatFecha(data.balanza.chofer.vencimiento_art)}</Descriptions.Item>
                        </Descriptions>
                      </Panel>
                    </Collapse>
                  )}

                  {/* Datos del Camión */}
                  {data.balanza.camion && (
                    <Collapse ghost style={{ marginTop: '12px' }}>
                      <Panel header="Datos del Camión" key="camion">
                        <Descriptions 
                          column={{ xs: 1, sm: 2, md: 2 }}
                          size="small"
                          labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                          contentStyle={{ fontSize: '13px' }}
                        >
                          <Descriptions.Item label="Patente Camión">{data.balanza.camion.patente_camion}</Descriptions.Item>
                          <Descriptions.Item label="Patente Acoplado">{data.balanza.camion.patente_acoplado}</Descriptions.Item>
                        </Descriptions>
                      </Panel>
                    </Collapse>
                  )}

                  {/* Datos del Ciclo */}
                  <Collapse ghost style={{ marginTop: '12px' }}>
                    <Panel header="Datos del Ciclo" key="ciclo">
                      <Descriptions 
                        column={{ xs: 1, sm: 2, md: 2 }}
                        size="small"
                        labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                        contentStyle={{ fontSize: '13px' }}
                      >
                        <Descriptions.Item label="Nombre">{data.ciclo.nombre || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Descripción">{data.ciclo.descripcion || '-'}</Descriptions.Item>
                      </Descriptions>
                    </Panel>
                  </Collapse>
                </div>
              ),
            },
            {
              key: 'detalles',
              label: 'Detalles',
              children: (
                <div style={{ paddingTop: '12px' }}>
                  {/* Tabla de Detalles */}
                  {data.detalles.length > 0 && (
                    <StandardCard 
                      title="Detalles de la Orden"
                      bodyStyle={{ padding: '12px' }}
                      style={{ marginBottom: '16px' }}
                    >
                      <Table
                        columns={detallesColumns}
                        dataSource={data.detalles.map((detalle, index) => ({ ...detalle, key: index }))}
                        pagination={false}
                        size="middle"
                        scroll={{ x: 'max-content' }}
                      />
                    </StandardCard>
                  )}

                  {/* Totales */}
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                      <StandardCard 
                        bodyStyle={{ padding: '16px', textAlign: 'center' }}
                        style={{ backgroundColor: '#f0f7ff' }}
                      >
                        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: theme.colors.primary }}>
                          Cantidad Fardos
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: 600 }}>{data.totales.cantidad_fardos}</div>
                      </StandardCard>
                    </Col>
                    <Col xs={24} sm={8}>
                      <StandardCard 
                        bodyStyle={{ padding: '16px', textAlign: 'center' }}
                        style={{ backgroundColor: '#f6ffed' }}
                      >
                        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#52c41a' }}>
                          Total Kilos Netos
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: 600 }}>
                          {data.totales.kilos_netos.toLocaleString('es-ES', { minimumFractionDigits: 2 })} kg
                        </div>
                      </StandardCard>
                    </Col>
                    <Col xs={24} sm={8}>
                      <StandardCard 
                        bodyStyle={{ padding: '16px', textAlign: 'center' }}
                        style={{ backgroundColor: '#e6f7ff' }}
                      >
                        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#1890ff' }}>
                          Cantidad Detalles
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: 600 }}>{data.totales.cantidad_detalles}</div>
                      </StandardCard>
                    </Col>
                  </Row>

                  {/* Observaciones y Datos Adicionales */}
                  {(data.observacion || data.buque || data.marca) && (
                    <StandardCard 
                      title="Información Adicional"
                      bodyStyle={{ padding: '16px' }}
                      style={{ marginTop: '16px' }}
                    >
                      {data.observacion && (
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Observaciones:</div>
                          <div style={{ fontSize: '13px' }}>{data.observacion}</div>
                        </div>
                      )}
                      <Descriptions 
                        column={{ xs: 1, sm: 2, md: 2 }}
                        size="small"
                        labelStyle={{ fontSize: '12px', fontWeight: 600 }}
                        contentStyle={{ fontSize: '13px' }}
                      >
                        {data.buque && <Descriptions.Item label="Buque">{data.buque}</Descriptions.Item>}
                        {data.marca && <Descriptions.Item label="Marca">{data.marca}</Descriptions.Item>}
                      </Descriptions>
                    </StandardCard>
                  )}
                </div>
              ),
            },
          ]}
        />
      </StandardCard>

    </div>
  );
};

export default EmisionRecdes;
